import { FormEvent, useEffect, useState } from 'react';
import { api } from '../lib/api';
import DictateButton from '../components/DictateButton';
import SimpleFolderTree, { SimpleFolderRow } from '../components/SimpleFolderTree';

type Note = {
  id: number;
  content: string;
  project_id: number | null;
  folder_id: number | null;
  created_at: string;
};

export default function PersonalSpace() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [folders, setFolders] = useState<SimpleFolderRow[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Note | null>(null);
  const [editText, setEditText] = useState('');

  async function refresh() {
    const [n, f] = await Promise.all([
      api.get<{ notes: Note[] }>('/private-notes'),
      api.get<{ folders: SimpleFolderRow[] }>('/private-folders'),
    ]);
    setNotes(n.notes);
    setFolders(f.folders);
  }
  useEffect(() => {
    refresh();
  }, []);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await api.post('/private-notes', {
        content: draft.trim(),
        project_id: null,
        folder_id: null,
      });
      setDraft('');
      refresh();
    } finally {
      setBusy(false);
    }
  }

  async function del(id: number) {
    if (!confirm('Supprimer cette note ?')) return;
    await api.del(`/private-notes/${id}`);
    refresh();
  }

  async function save() {
    if (!editing) return;
    await api.patch(`/private-notes/${editing.id}`, { content: editText });
    setEditing(null);
    refresh();
  }

  async function addFolder(name: string, parentId: number | null) {
    await api.post('/private-folders', { name, parent_id: parentId });
    refresh();
  }
  async function renameFolder(id: number, name: string) {
    await api.patch(`/private-folders/${id}`, { name });
    refresh();
  }
  async function removeFolder(id: number) {
    await api.del(`/private-folders/${id}`);
    refresh();
  }
  async function moveNote(noteId: number, folderId: number | null) {
    await api.patch(`/private-notes/${noteId}/move`, { folder_id: folderId });
    refresh();
  }

  return (
    <>
      <div className="topbar">
        <h1>Espace perso</h1>
        <span className="card-meta">{notes.length} notes</span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-meta" style={{ marginBottom: 6 }}>
          Tes notes privées, transversales (pas liées à un projet). Personne d'autre ne les voit.
        </div>
        <form onSubmit={add} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Une idée, une question, un rappel…"
            style={{
              flex: 1,
              padding: 10,
              border: '1px solid var(--c-border)',
              borderRadius: 6,
              background: 'var(--c-bg)',
              resize: 'vertical',
            }}
          />
          <DictateButton value={draft} onChange={setDraft} />
          <button className="btn btn-primary" disabled={busy}>
            {busy ? '…' : 'Ajouter'}
          </button>
        </form>
      </div>

      <SimpleFolderTree
        folders={folders}
        items={notes}
        headerLabel="📚 Mes dossiers"
        rootLabel="Notes non classées"
        onAddFolder={addFolder}
        onRenameFolder={renameFolder}
        onDeleteFolder={removeFolder}
        onMoveItem={moveNote}
        renderItem={(n) => (
          <div className="note-row">
            <div className="note-content" onClick={() => {
              setEditing(n);
              setEditText(n.content);
            }}>
              <div className="note-text">{truncate(n.content, 200)}</div>
              <div className="card-meta" style={{ fontSize: 11, marginTop: 2 }}>
                {new Date(n.created_at).toLocaleString('fr-FR')}
                {n.project_id ? ' · (notes perso d\'un projet)' : ''}
              </div>
            </div>
            <button
              onClick={() => del(n.id)}
              className="note-del"
              title="Supprimer"
            >
              ×
            </button>
          </div>
        )}
      />

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="card-meta">Note · {new Date(editing.created_at).toLocaleString('fr-FR')}</div>
            <textarea
              rows={10}
              autoFocus
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                marginTop: 8,
                border: '1px solid var(--c-border)',
                borderRadius: 6,
                background: 'var(--c-bg)',
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button className="btn btn-ghost" onClick={() => setEditing(null)}>
                Annuler
              </button>
              <button className="btn btn-primary" onClick={save}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n) + '…';
}
